import { Visibility, VisibilityOff } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Box, Button, Checkbox, Collapse, FormControlLabel, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from '../../assets/logos/icare_fulllogo.png';
import { AppContext } from '../../Base';
import { ButtonContentWithLoader } from '../../components/inputs/ButtonContentWithLoader';
import { isValidEmail, sendReactGAPageView } from "../../consts/helpers";
import useAuthUser from '../../hooks/useAuthUser';

export default function LoginPage() {
	const { state, pathname } = useLocation();
    var { isAuthView }  = useAuthUser();
	useEffect(() => {
        sendReactGAPageView(pathname, 'Log In');
    }, [pathname])

    const setSessionCookie = useContext(AppContext).setSessionCookie;
    const navigate = useNavigate();

    // Inputs
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false); 

    // Stage flags
    const [isLoading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const handleClickShowPassword = () => setShowPassword(!showPassword);

    // Response flags
    const [isAlertOpen, setOpen] = useState(false);
    const [apiResponse, setResponse] = useState({});
    const setAlertResponse = (severity, message) => {
        setResponse({ severity: severity, message: message });
        setOpen(Boolean(message));
        setTimeout(() => {
            setOpen(false);
        }, 20000);
    }

    const url = process.env.REACT_APP_USERACCESS_LAMBDA;
    const handleLoginAttempt = (e) => {
        e.preventDefault()

        if (email === "" || password === "") {
            setAlertResponse('warning', "Please enter both your email and password");
            return;
        }
        if (!isValidEmail(email)) {
            setAlertResponse('error', "Please enter a valid email address");
            return;
        }
        setLoading(true);
        fetch(url, {
            method: 'POST',
            body: JSON.stringify({
                'email': email,
                'password': password,
                'rememberMe': rememberMe,
            }),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        }).then(
            function (response) {
                return response.json();
            }
        ).then(
            function(data) {
                setLoading(false);
                setAlertResponse('success','')
                if (data?.loggedIn) {
                    setSessionCookie(data?.userData.email, data?.token)
                    navigate('/');
                }
                setAlertResponse('error', data?.message);
            }
        ).catch(
            function(error) {
                setAlertResponse('error', error);
            }
        );
    }

    useEffect(() => {
        if (isAuthView) navigate('/');
        else {
            if (state) setAlertResponse(state?.severity || 'error', state?.message);
        }
    }, [isAuthView])

    return (
        <Stack spacing={3} sx={{ padding: 2 }}>
            <Box>
                <img src={logo} alt='icare app logo' style={{ maxWidth: 300 }}/>
            </Box>
            <Typography variant="h4" align="center" className='green-text-color'>Login</Typography>
            <>
                 <TextField
                    className='rounded-input' 
                    fullWidth
                    label="Email"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                    margin="normal"
                    required
                    error={!isValidEmail(email) && email.length > 0}  
                    helperText={!isValidEmail(email) && email.length > 0 ? "Please enter a valid email address" : ""} 
                />
                <TextField
                    className='rounded-input' 
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    // type="password"
                    variant="outlined"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    margin="normal"
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                >
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                                </InputAdornment>
                            ),
                        }
                    }}
                    required
                />
                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                        }
                        label={
                            <Typography variant="body2">
                                Keep me logged in
                            </Typography>
                        }
                    />
                    <Button 
                        className={`pill_btn ${email && password ? 'pri_btn' : ''}`}
                        fullWidth 
                        onClick={handleLoginAttempt}
                        disabled={!email || !password}
                    >
                        { ButtonContentWithLoader(isLoading, 'Login') }
                    </Button>
                </Box>
                <Collapse in={isAlertOpen}>
                    <Alert
                    action={
                        <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => {
                            setOpen(false);
                        }}
                        >
                        <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                    sx={{ mt: 2, borderRadius: '24px' }}
                    severity={apiResponse.severity}
                    >
                    {apiResponse.message}
                    </Alert>
                </Collapse>
                <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                    <Link to="/login/reset-password"> Forgot Your Password? </Link>
                </Typography>
                <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                    Don't have an account? <Link to="/new-user">Sign up here!</Link>
                </Typography>
            </>    
        </Stack>
    );
}
